import React, { useEffect, useRef, useState } from "react"
import { Col, Container, OverlayTrigger, Row, Tooltip } from "react-bootstrap"
import styled from "styled-components"
import { Category, Channel, Company, Description, EntityType, EventDate, Link, Person, Product, ShareTargetType, SocialMedia, Trademark, News } from "../../../backendServices/Types"
import branding from "../../../branding/branding"
import { IconCameraJoin, IconCoupon, IconFacebook, IconInstagram, IconLink, IconLinkedIn, IconLiveStream, IconLocation, IconShare, IconShareLink, IconTwitter, IconVideo, IconXing, IconYouTube, IconPinterest, IconContact, IconInfo, IconLock, IconFacebookOriginal, IconLinkedInOriginal, IconXingOriginal, IconInstagramOriginal, IconYouTubeOriginal, IconPinterestOrginal } from "../../../ui/Icons"
import { ContentScrollContainer } from "../../../ui/ScrollContainer"
import Linkify from "react-linkify"
import { usePrivacyPolicyModal } from "../../../ui/PrivacyPolicyModal"
import { BasisPremiumType, getBasisPremiumConfig } from "../../../branding/BasisPremiumBranding"
import { useLanguageState } from "../../../globalStates/LanguageState"
import { useMeetingContext } from "../../../conference/context/MeetingContext"
import { useChimeContext } from "../../../conference/context/ChimeContext"
import { useLoggedInState } from "../../../globalStates/LoggedInUser"
import { isExplorationOrPostEventPhase, isExplorationPhase, isLivePhase } from "../../../utils/EventPhaseChecker"
import { Staff, StaffEntry, StaffType } from "../../../ui/StaffEntry"
import { trackVisit, getAccessStatusForAllVirtualCafes, UserRestrictedAreaAccess, BackendServiceError, getAccessStatusForAllConferenceRooms } from "../../../backendServices/BackendServices"
import ScrollBarComponent from "../../../ui/ScrollBar"
import { FormattedDateExtensionDiv, FormattedDatePar, HeaderTitle } from "./StyledComponents"
import { useRecommendOrganizationModal } from "../../../ui/RecommendOrganizationModal"
import { isEventDateLive } from "../../../utils/DateUtils"
import { useCategoriesState } from "../../../globalStates/CategoriesState"
import { buildDetailLink, DetailNavLink } from "../DetailNavLink"
import { PointsBadge } from "../../program/EventDateEntry"
import BookmarkWithToggle from "../../../ui/BookmarkWithToggle"
import LiveEvent from "../../../ui/LiveEvent"
import { DetailPageEntityRows, EntityRowData, DetailPageEntityRow } from "./DetailPageEntityRows"
import MeetingRoomGroup from "../../lounges/MeetingRoomGroup"
import { Media } from "./Media"
import moment from "moment"
import TileRow from "../../../ui/TileRow"
import { useAppState } from "../../../globalStates/AppState"
import Categories from "./Categories"
import { getIamPartOf } from "../../../globalStates/IAmPartOf"
import queryString from 'query-string'
import VizSensor from 'react-visibility-sensor'
import { format } from "date-fns"
import { hasAvailableStaff, joinShowroom } from "../OrganizationDetailPageContent"
import { SocialMediaType } from "../../myprofile/MyProfilePageContentBranding"
import { DetailSection, InfoContentOrderType } from "../DetailPageBranding"
import { useLocation } from "react-router-dom"
import VideoPlayerReal from "../../videoPlayer/VideoPlayerReal"

const germanLocale = require('date-fns/locale/de');

const allowedNumberOfItemsMap = {
    [BasisPremiumType.NONE]: {
        "products": branding.organizationDetailPageContent.numberOfProductsNone,
        "trademarks": branding.organizationDetailPageContent.numberOfTrademarksNone,
        "news": branding.organizationDetailPageContent.numberOfNewsNone
    },
    [BasisPremiumType.BASIC]: {
        "products": branding.organizationDetailPageContent.numberOfProductsBasic,
        "trademarks": branding.organizationDetailPageContent.numberOfTrademarksBasic,
        "news": branding.organizationDetailPageContent.numberOfNewsBasic
    },
    [BasisPremiumType.STANDARD]: {
        "products": branding.organizationDetailPageContent.numberOfProductsStandard,
        "trademarks": branding.organizationDetailPageContent.numberOfTrademarksStandard,
        "news": branding.organizationDetailPageContent.numberOfNewsStandard
    },
    [BasisPremiumType.PREMIUM]: {
        "products": branding.organizationDetailPageContent.numberOfProductsPremium,
        "trademarks": branding.organizationDetailPageContent.numberOfTrademarksPremium,
        "news": branding.organizationDetailPageContent.numberOfNewsPremium
    },
    [BasisPremiumType.ULTIMATE]: {
        "products": branding.organizationDetailPageContent.numberOfProductsUltimate,
        "trademarks": branding.organizationDetailPageContent.numberOfTrademarksUltimate,
        "news": branding.organizationDetailPageContent.numberOfNewsUltimate
    },
    [BasisPremiumType.SPONSOR]: {
        "products": -1,
        "trademarks": -1,
        "news": -1
    }
}

interface OrgaEntities {
    products: Product[]
    trademarks: Trademark[]
    news: News[]
}

const filterEntities = (organization: Company): OrgaEntities => {
    const allowedNumberOfItemsMapObject = allowedNumberOfItemsMap[organization.basisPremium as BasisPremiumType]
    const allowedNumProducts = allowedNumberOfItemsMapObject?.products
    const allowedNumTrademarks = allowedNumberOfItemsMapObject?.trademarks
    const allowedNumNews = allowedNumberOfItemsMapObject?.news
    let productsFiltered = organization.products
    let trademarksFiltered = organization.trademarks
    let newsFiltered = organization.news

    if (allowedNumProducts && allowedNumProducts > -1)
        productsFiltered = productsFiltered?.slice(0, allowedNumProducts)
    if (allowedNumTrademarks && allowedNumTrademarks > -1)
        trademarksFiltered = trademarksFiltered?.slice(0, allowedNumTrademarks)
    if (allowedNumNews && allowedNumNews > -1)
        newsFiltered = newsFiltered?.slice(0, allowedNumNews)

    return {
        products: productsFiltered,
        trademarks: trademarksFiltered,
        news: newsFiltered
    }
}
interface OrgaTransformedEntities {
    products: EntityRowData[]
    trademarks: EntityRowData[]
    news: EntityRowData[]
}

const mapEntities = (entities: OrgaEntities): OrgaTransformedEntities => {
    const products = entities.products?.map(product => product as EntityRowData)
    const trademarks = entities.trademarks?.map(trademark => trademark as EntityRowData)
    const news = entities.news?.map(news => {
        const entityRow = news as EntityRowData
        entityRow.name = news.title
        entityRow.description = { teaser: news.teaser }
        return entityRow
    })

    return {
        products: products,
        trademarks: trademarks,
        news: news
    }
}


const transformOrgaSubEntities = (organization: Company): OrgaTransformedEntities => {
    const filteredEntites = filterEntities(organization)
    return mapEntities(filteredEntites)
}


const OrganizationDetailPageContentRoot = styled.div`
    padding: 0px;
    font-family: ${branding.font1};

    @media(max-width: 1400px) {
       padding: 0px 100px;
    }

`

const CompanyLogoDiv = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #D9D9D9;
    border-radius: 5px;
    width: 150px;
    height: 150px;
    padding: 20px;
    background: #fff;

    @media(max-width: 1600px) {
        width: 120px;
        height: 120px;
        padding: 25px;
    }

    @media(max-width: 1400px) {
        width: 100px;
        height: 100px;
        padding: 15px;
    }

    @media(max-width: 1200px) {
        width: 80px;
        height: 80px;
        padding: 10px;
    }
`

const CompanyLogo = styled.img`
    object-fit: contain;
    width: 100%;
    height: 100%;
`

const RowTileParent = styled.div`
`

const RemoteLivestreamTile = styled.div`
    position: relative;
    height: 16.875vw; // width * 0.5625 - aspect ratio 16:9
    width: 30vw;
    color: white !important;
    margin: 0px 10px 20px 0px;
    border: 1px solid #000;
    border-radius: 5px;
`

interface DetailPageSectionsProps {
    type: EntityType
    guestUserBannerRef?: React.RefObject<HTMLDivElement>
    handleScroll?(value: number): void
    onAccessDenied?: () => void
    entity: Company | Product | Trademark | News
    adjustHeightWith?: number
    setShowGuestModal?: (value: boolean) => void
}

const DetailPageSections: React.FunctionComponent<DetailPageSectionsProps> = (props) => {
    const strings = useLanguageState().getStrings()
    const appState = useAppState()
    const lang = useLanguageState().getLanguage()

    //Main content container reference
    const containerRef = useRef<HTMLDivElement>(null)
    const [containerOffsetLeft, setContainerOffsetLeft] = useState<number | undefined>()
    const isNetworkingOpen = appState.isNetworkingOpen()

    useEffect(() => {
        setContainerOffsetLeft(containerRef.current?.offsetLeft)
    }, [window.innerWidth, isNetworkingOpen]) // eslint-disable-line

    const [accessList, setAccessList] = useState<UserRestrictedAreaAccess[]>([])
    const [conferenceRoomAccessList, setConferenceRoomAccessList] = useState<UserRestrictedAreaAccess[]>([])
    const [meetingRoomGroups, setMeetingRoomGroups] = useState<any[]>([])

    let sectionOrder: DetailSection[] = []
    let infoContentOrder: InfoContentOrderType[] = []
    let organizationId: string | undefined
    let organization: Company | undefined
    let product: Product | undefined
    let trademark: Trademark | undefined
    let news: News | undefined
    let organizations: Company[] = []
    let products: EntityRowData[] = []
    let trademarks: EntityRowData[] = []
    let orgaNews: EntityRowData[] = []
    let liveChannels: Channel[] = []
    let liveSessions: EventDate[] = [] // for live eventdates without channel (e.g. roundtable)
    let previousSessions: EventDate[] = []
    let upcomingSessions: EventDate[] = []
    let iframe: string
    let config: any;
    let categories: Category[] = []

    useEffect(() => {
        getAccessStatusForAllVirtualCafes()
            .then((response) => {
                if ((response as BackendServiceError).httpStatus) {

                } else {
                    setAccessList(response as UserRestrictedAreaAccess[])
                    setMeetingRoomGroups(strings.meetingRoomGroups)
                }
            })
        getAccessStatusForAllConferenceRooms().then((response) => {
            if ((response as BackendServiceError).httpStatus) {

            } else {
                setConferenceRoomAccessList(response as UserRestrictedAreaAccess[])
            }
        })
        // eslint-disable-next-line
    }, [])


    switch (props.type) {
        case "organization":
            organization = props.entity as Company
            config = getBasisPremiumConfig(organization?.basisPremium! as BasisPremiumType);
            const orgaEntities = transformOrgaSubEntities(organization)
            products = orgaEntities.products
            trademarks = orgaEntities.trademarks
            orgaNews = orgaEntities.news
            iframe = lang === "en" ? organization.iframeUrl : organization.iframeUrlDe
            sectionOrder = branding.organizationDetailPageContent.orgaDetailPageMainContentOrder
            infoContentOrder = branding.organizationDetailPageContent.orgaDetailPageInfoSectionOrder
            organizationId = props.entity.id
            categories = organization.categories
            const now = moment()
            if (organization.eventDates) {
                organization.eventDates.forEach(eventDate => {
                    if (isEventDateLive(eventDate)) {
                        if (eventDate.channel) {
                            const channel: Channel = { ...eventDate.channel, currentEventDate: eventDate }
                            liveChannels.push(channel)
                        }
                        else
                            liveSessions.push(eventDate)
                    } else if (moment(eventDate.date + " " + eventDate.start).isAfter(now)) {
                        upcomingSessions.push(eventDate)
                    } else if (moment(eventDate.date + " " + eventDate.end).isBefore(now)) {
                        previousSessions.push(eventDate)
                    }
                })
            }
            break;
        case "product":
            product = props.entity as Product
            organizations = product.organizations
            sectionOrder = branding.productDetailPageContent.sections
            infoContentOrder = branding.productDetailPageContent.infoContentOrder
            categories = product.categories
            if ((props.entity as Product).organizations?.length)
                organizationId = (props.entity as Product).organizations[0].id
            break;
        case "trademark":
            trademark = props.entity as Trademark
            organizations = trademark.organizations
            sectionOrder = branding.trademarkDetailPageContent.sections
            infoContentOrder = branding.trademarkDetailPageContent.infoContentOrder
            categories = trademark.categories
            if ((props.entity as Trademark).organizations?.length)
                organizationId = (props.entity as Trademark).organizations[0].id
            break;
        case "news":
            news = props.entity as News
            organizations = [news.organization]
            sectionOrder = branding.newsDetailPageContent.sections
            infoContentOrder = branding.newsDetailPageContent.infoContentOrder
            categories = news.categories
            if (news.organization)
                organizationId = news.organization.id
            break;

        default: return null
    }

    return <>
        <ContentScrollContainer handleScroll={props.handleScroll} adjustForHeaderWith={props.guestUserBannerRef && props.guestUserBannerRef.current ? (props.guestUserBannerRef.current.clientHeight + (props.adjustHeightWith ?? 0)) + "px" :
            ((props.adjustHeightWith ?? 0) + "px")}>
            <OrganizationDetailPageContentRoot>
                <Container ref={containerRef} className="mt-5" style={{ color: branding.mainInfoColor, width: '100%' }}>
                    {sectionOrder.map((item, index) => {

                        /* Company logo and detail information */
                        if (item === DetailSection.DETAIL_INFO) {
                            return <DetailInfo key={index} order={infoContentOrder} company={organization} conferenceRoomAccessList={conferenceRoomAccessList} containerOffsetLeft={containerOffsetLeft} {...props.entity} />
                        }

                        /* Company team */
                        if (item === DetailSection.TEAM && organization?.persons?.length) {
                            return (
                                config.teamMembersVisible && <Row key={index} className="mt-5 mb-5">
                                    <TeamSessionsTemplate
                                        type="team"
                                        team={organization.persons}
                                        organizationId={organization.id}
                                        organizationName={organization.name}
                                        organization={organization}
                                        onAccessDenied={props.onAccessDenied} />
                                </Row>
                            )
                        }

                        /* Now live section */
                        if (item === DetailSection.LIVE_SESSIONS && (liveChannels.length || liveSessions.length)) {
                            return (
                                <>
                                    {liveChannels && liveChannels.length > 0 && <RowTileParent key={index}>
                                        <TileRow
                                            title={strings.receptionPage.liveSessions}
                                            titleNoLink={strings.receptionPage.nowLive + ' (' + liveChannels.length + ')'}
                                            type="orgaDetail"
                                            tileRowMargin="0"
                                            offsetLeft={containerOffsetLeft}
                                            scrollBar
                                            childWidth={480 + 20}
                                            childCount={liveChannels.length}
                                        >
                                            {liveChannels.map((channel, index) =>
                                                <VizSensor key={index} partialVisibility={true}>
                                                    {({ isVisible }) => isVisible ?
                                                        <LiveEvent
                                                            key={index}
                                                            id={index.toString()}
                                                            title={channel.name}
                                                            thumbnailUrl={channel.thumbnailUrl}
                                                            channelId={channel.id}
                                                            channelIcon={IconLiveStream({ fill: "#fff" })}
                                                            eventDate={channel.currentEventDate}
                                                            setShowGuestModal={props.setShowGuestModal!}
                                                        />
                                                        : <div style={{ width: 480, height: 270 }}></div>
                                                    }
                                                </VizSensor>
                                            )}
                                        </TileRow>
                                    </RowTileParent>
                                    }
                                    {
                                        liveSessions && liveSessions.length > 0 &&
                                        <Row key={index} className="mt-5 pb-5">
                                            <TeamSessionsTemplate
                                                type="session"
                                                sessions={liveSessions}
                                                organizationId={organizationId!}
                                                organization={organization!}
                                                live={true} />
                                        </Row>
                                    }
                                </>
                            )
                        }

                        /* Upcoming sessions */
                        if (item === DetailSection.UPCOMING_SESSIONS && upcomingSessions?.length) {
                            return (<Row key={index} className="mt-5 pb-5">
                                <TeamSessionsTemplate
                                    type="session"
                                    sessions={upcomingSessions}
                                    organizationId={organizationId!}
                                    organization={organization!}
                                    past={false} />
                            </Row>
                            )
                        }

                        /* Previous sessions */
                        if (item === DetailSection.PREVIOUS_SESSIONS && previousSessions?.length) {
                            return (<Row key={index} className="mt-5 pb-5">
                                <TeamSessionsTemplate
                                    type="session"
                                    sessions={previousSessions}
                                    organizationId={organizationId!}
                                    organization={organization!}
                                    past={true}
                                />
                            </Row>
                            )
                        }

                        /* Categories */
                        if (item === DetailSection.CATEGORIES && categories && categories.length) {
                            return (
                                <Categories key={index} categories={categories} categoryType={props.type} />
                            )
                        }

                        /* Virtual cafes */
                        if (item === DetailSection.VIRTUAL_CAFES && organization) {
                            if (organization.basisPremium === BasisPremiumType.NONE)
                                return null

                            const orgaMeetingRoomGroups = meetingRoomGroups.filter(x => x.organizationId === organization!.id || x.secondOrganizationId === organization!.id)
                            return (
                                orgaMeetingRoomGroups.length !== 0 && <RowTileParent key={index}>
                                    <TileRow
                                        title={strings.organizationDetailPageContent.virtualCafesTitle}
                                        height="220px"
                                        offsetLeft={containerOffsetLeft}
                                        scrollBar
                                        childWidth={500 + 4}
                                        childCount={orgaMeetingRoomGroups.length}
                                        hideShadows={true}
                                    >
                                        {orgaMeetingRoomGroups.map(meetingRoomGroup =>
                                            <MeetingRoomGroupContainer>
                                                <MeetingRoomGroup key={meetingRoomGroup.id} source="ORG_DETAIL" accessStatus={(accessList.find((e: UserRestrictedAreaAccess) => e.restrictedAreaId === meetingRoomGroup.id))?.status || " "} meetingRoomGroupConfig={meetingRoomGroup} roomType="virtualCafe" />
                                            </MeetingRoomGroupContainer>
                                        )}
                                    </TileRow>
                                </RowTileParent>
                            )
                        }

                        /* Company Links */
                        if (item === DetailSection.LINKS && props.entity.links?.length) {
                            return (<Row key={index} className="mt-5 pb-5">
                                <Links
                                    organizationId={organizationId}
                                    organization={organization}
                                    links={props.entity.links}
                                    tooltipMessage={"Copy to clipboard"}
                                />
                            </Row>
                            )
                        }

                        /* Media */
                        if (item === DetailSection.MEDIA && props.entity.attachments?.length) {
                            return (<Row key={index} className="mt-5 pb-5">
                                <Media
                                    media={props.entity.attachments}
                                    organizationId={organizationId}
                                    organization={organization!}
                                />
                            </Row>
                            )
                        }

                        /* Contact information */
                        if (item === DetailSection.COMPANY_INFORMATION_TITLE) {
                            return (
                                <Row key={index} className="mt-5 pb-1" style={{ marginLeft: "5px", marginRight: "5px" }}>
                                    <HeaderTitle>{strings.organizationDetailPageContent.companyInformationTitle}</HeaderTitle>
                                </Row>
                            )
                        }

                        /* Founding year, size, industry */
                        if (item === DetailSection.COMPANY_DETAILS && organization) {
                            return (
                                <div key={index} className="mt-3 pb-5">
                                    <CompanyDetails company={organization} />
                                </div>
                            )
                        }

                        /* Parent exhibitor */
                        if (item === DetailSection.PARENT && organization && organization.parent) {
                            const rowData: EntityRowData = organization.parent
                            return <>
                                <div key={index} className="p-0 mb-4">
                                    <HeaderTitle>{strings.organizationDetailPageContent.parentSectionTitle}</HeaderTitle>
                                </div>
                                <DetailPageEntityRow type={"organization"} pageType={props.type} rowData={rowData} key={rowData.id} />
                            </>
                        }

                        /* Co-Exhibitors */
                        if (item === DetailSection.CO_EXHIBITORS && organization && organization.children && organization.children.length > 0) {
                            return <DetailPageEntityRows key={index} type="organization" pageType={props.type} headerTitle={strings.organizationDetailPageContent.coExhibitorsSectionTitle} rowsData={organization.children} />
                        }

                        if (item === DetailSection.COUPONS && organization?.coupons?.length) {
                            return <>
                                {
                                    <Row key={index} className="mt-5 pb-5">
                                        <TeamSessionsTemplate
                                            type="coupon"
                                            coupons={organization.coupons}
                                            organizationId={organization.id}
                                            organization={organization}
                                        />
                                    </Row>
                                }
                            </>
                        }

                        if (item === DetailSection.PRODUCTS && products?.length && (branding.configuration.detailSectionToggle === false || organization?.showProducts)) {
                            return <DetailPageEntityRows key={index} type="product" pageType={props.type} headerTitle={strings.sideIconBar.productsSearchDrawerText} rowsData={products} />
                        }

                        if (item === DetailSection.TRADEMARKS && trademarks?.length && (branding.configuration.detailSectionToggle === false || organization?.showTrademarks)) {
                            return <DetailPageEntityRows key={index} type="trademark" pageType={props.type} headerTitle={strings.sideIconBar.trademarksSearchDrawerText} rowsData={trademarks} />
                        }

                        if (item === DetailSection.NEWS && orgaNews && orgaNews.length > 0) {
                            return <DetailPageEntityRows key={index} type="news" pageType={props.type} headerTitle={strings.sideIconBar.newsSearchDrawerText} rowsData={orgaNews} />
                        }

                        if (item === DetailSection.ORGANIZATIONS && organizations?.length) {
                            return <DetailPageEntityRows key={index} type="organization" pageType={props.type} headerTitle={strings.sideIconBar.companiesSearchDrawerText} rowsData={organizations} />
                        }

                        if (item === DetailSection.SHOWCASE && iframe) {
                            return <IframeSection src={iframe} title={organization?.id + "-iframe"} />
                        }

                        if (item === DetailSection.REMOTE_LIVESTREAMS && organization && organization.remoteLivestreams && organization.remoteLivestreams.length > 0) {
                            const sectionTitle = strings.organizationDetailPageContent.remoteLivestreamsTitle.replace("$count", organization.remoteLivestreams.length.toString())
                            return (
                                <RowTileParent key={index}>
                                    <TileRow
                                        title={sectionTitle}
                                        titleNoLink={sectionTitle}
                                        tileRowMargin="50px 0"
                                        offsetLeft={containerOffsetLeft}
                                        scrollBar
                                        // converting vw to px: (window.innerWidth * RemoteLivestreamTile.width(30vw)) / 100
                                        // childWidth: Width of the RemoteLivestreamTile in px + 10px margin
                                        childWidth={((window.innerWidth * 30) / 100) + 10}
                                        childCount={organization.remoteLivestreams.length}
                                    >
                                        {organization.remoteLivestreams.map((stream, idx) =>
                                            <RemoteLivestreamTile key={idx}>
                                                <VideoPlayerReal
                                                    src={stream.url}
                                                    controls={true}
                                                    live={true}
                                                    remoteLivestream={stream}
                                                    remoteStreamLayout={true} />
                                            </RemoteLivestreamTile>
                                        )}
                                    </TileRow>
                                </RowTileParent>
                            )
                        }


                        return null
                    })
                    }


                </Container>
            </OrganizationDetailPageContentRoot>
        </ContentScrollContainer>
    </>
}

export default DetailPageSections

/* #region Company address, phone, email info */
interface CompanyAddressPhoneEmailProps {
    company: Company
}
const CompanyAddressPhoneEmail: React.FunctionComponent<CompanyAddressPhoneEmailProps> = (props) => {

    const componentDecorator = (href: string, text: string, key: number) => (
        <a style={{ fontWeight: "bold", color: branding.mainInfoColor }} href={href} key={key} target="_blank" rel="noopener noreferrer">
            {text}
        </a>
    );

    return (

        <div style={{ display: 'flex', flex: 2 }}>

            <CompanyAdditionalInfoDiv style={{ flex: 1, display: 'flex', flexDirection: 'row' }}>
                <div style={{ paddingRight: '10px' }}>
                    {(props.company.name || props.company.adress1 || props.company.postCode || props.company.country) && IconLocation({ fill: branding.sideIconBar.sideIconColorDark, width: '20', height: '20' })}
                </div>
                <div>
                    <p>{props.company.name}</p>
                    <p>{[props.company.adress1, props.company.adress2, props.company.adress3].filter(Boolean).join(" | ")}</p>
                    <p>{props.company.postCode} {props.company.city}</p>
                    <p>{props.company.country}</p>
                </div>
            </CompanyAdditionalInfoDiv>



            <CompanyAdditionalInfoDiv style={{ flex: 1, display: 'flex', flexDirection: 'row' }}>
                <div style={{ paddingRight: '10px' }}>
                    {(props.company.phone || props.company.email || props.company.web) && IconContact({ fill: branding.sideIconBar.sideIconColorDark, width: '20', height: '20' })}
                </div>
                <div>
                    {props.company.phone && !branding.organizationDetailPageContent.hideTelephone && <p>{props.company.phone}</p>}
                    {props.company.email && !branding.organizationDetailPageContent.hideEmailAddress && <p><Linkify componentDecorator={componentDecorator}>{props.company.email}</Linkify></p>}
                    {props.company.web && <p><Linkify componentDecorator={componentDecorator}>{props.company.web}</Linkify></p>}
                </div>
            </CompanyAdditionalInfoDiv>
        </div>


    )
}
/* #endregion */

/* #region  Company Detail Info */
const CompanyTeaser = styled.p`
    font-size: ${branding.organizationDetailPageContent.companyTeaserFontSize};
    font-weight: 400;
    white-space: pre-line;
`

const TalkWithTeamDiv = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    margin-top: 50px;
    border: 1px solid ${branding.mainInfoColor};
    border-radius: 5px;
    height: 100px;
    font-size: 16px;
    position: relative;

    @media(max-width: 1600px){
        font-size: 14px;
    }
`

const JoinButtonCol = styled(Col)`
    background: ${branding.mainInfoColor};
    font-family: ${branding.font1};
    border-radius: 25px;
    height: 70%; 
    color: #FFF;
    display: flex; 
    justify-content: center;
    align-items: center; 
    cursor: pointer;
    ${props => props.disabled ? "opacity: 0.5; pointer-events: none;" : ""}
`
const ExplorationPhaseMessage = styled.div`
    position: absolute;
    bottom: 0;
    left: 110px;
    color: rgb(189, 189, 189);
    font-family: ${branding.font1};
    @media(max-width: 1600px){
        left: 80px;
        font-size: 14px;
    }

    @media(max-width: 1400px){
        font-size: 12px;
        left: 50px;
    }
`

const MeetingRoomGroupContainer = styled.div`
    margin-bottom: 1.25rem;
`
interface DetailInfoProps {
    order: InfoContentOrderType[]
    company?: Company
    description?: Description
    pictureURL?: string
    name?: string
    initials?: string
    socialMedias?: SocialMedia[]
    conferenceRoomAccessList: UserRestrictedAreaAccess[]
    containerOffsetLeft: number | undefined
}
const DetailInfo: React.FunctionComponent<DetailInfoProps> = (props) => {
    const chime = useChimeContext()
    const meeting = useMeetingContext()
    const strings = useLanguageState().getStrings()
    const loggedInUser = useLoggedInState().user()
    const { showPrivacyPolicyModal, PrivacyModal } = usePrivacyPolicyModal()
    const queryParams: any = queryString.parse(window.location.search)
    const company = props.company
    let config = getBasisPremiumConfig(props.company?.basisPremium! as BasisPremiumType);
    return (
        <Row>
            <Col xs={2}>
                <CompanyLogoDiv>
                    {props.pictureURL ?
                        <CompanyLogo src={props.pictureURL} alt="" /> :
                        (branding.exhibitorsPageContent.defaultLogoVisibleOnCompanyTiles ? <CompanyLogo src={"/branding/rsz_defaultgrey.png"} /> : <h2>{props.initials}</h2>)}
                </CompanyLogoDiv>
            </Col>
            <Col xs={10}>
                <h3 style={{ fontSize: "22px", fontWeight: 800 }}>{props.name}</h3>
                {
                    props.order.map((item, index) => {

                        if (item === InfoContentOrderType.COMPANY_DETAILS && company) {
                            return (
                                <div key={index} className="mt-3">
                                    <CompanyDetails company={company} />
                                </div>
                            )
                        }

                        if (item === InfoContentOrderType.SOCIAL_MEDIA && props.socialMedias?.length) {
                            return <CompanySocialMedia key={index} socialMedias={props.socialMedias} />
                        }

                        if (item === InfoContentOrderType.DESCRIPTION && props.description?.text) {
                            return <CompanyTeaser key={index} className="mt-3">{props.description?.text.split('\\n').map((item, i) => <span key={i}>{item}<br /></span>)}</CompanyTeaser>

                        }

                        if (item === InfoContentOrderType.TALK_WITH_TEAM &&
                            (isExplorationOrPostEventPhase || isLivePhase) && !branding.organizationDetailPageContent.removeExpoShowroomDuringPostEvent &&
                            company && config.expoShowroomEnabled && hasAvailableStaff(company)) {
                            return <TalkWithTeamDiv key={index}>
                                <Col xs={1}>{IconCameraJoin({ width: "30", height: "30", fill: branding.sideIconBar.sideIconColorDark })}</Col>
                                <Col style={{ fontSize: "16px", fontWeight: 500 }} xs={strings.organizationDetailPageContent.joinButton.length > 14 ? 8 : 9}>{strings.organizationDetailPageContent.talkWithTeamBefString} {company.name} {strings.organizationDetailPageContent.talkWithTeamAftString}</Col>
                                <JoinButtonCol xs={strings.organizationDetailPageContent.joinButton.length > 14 ? 3 : 2} onClick={() => showPrivacyPolicyModal(company, (accepted) => {
                                    joinShowroom(company, loggedInUser!, chime, meeting)
                                })} disabled={!hasAvailableStaff(company) || (isExplorationOrPostEventPhase && !(getIamPartOf(queryParams) === "onboarding"))}>
                                    <p className="user-select-none" style={{ fontSize: strings.organizationDetailPageContent.joinButton.length > 14 ? "12px" : "16px", marginBottom: "0px" }}>{strings.organizationDetailPageContent.joinButton}</p>
                                </JoinButtonCol>
                                {isExplorationOrPostEventPhase &&
                                    <ExplorationPhaseMessage className="mt-2">{isExplorationPhase ? strings.organizationDetailPageContent.explorationPhasePlaceholder : strings.organizationDetailPageContent.postPhasePlaceholder}</ExplorationPhaseMessage>}
                            </TalkWithTeamDiv>
                        }

                        if (item === InfoContentOrderType.COMPANY_DETAILS && company) {
                            return (
                                <CompanyDetails key={index} company={company} />
                            )
                        }

                        if (item === InfoContentOrderType.CONFERENCE_ROOMS && company?.conferenceRooms && company?.conferenceRooms.length > 0) {
                            return (
                                <RowTileParent key={index} style={{ marginLeft: "-180px" }}>
                                    <TileRow
                                        title={strings.organizationDetailPageContent.virtualCafesTitle}
                                        height="220px"
                                        offsetLeft={props.containerOffsetLeft}
                                        scrollBar
                                        childWidth={510}
                                        childCount={company.conferenceRooms.length}
                                    >
                                        {company.conferenceRooms.map(conferenceRoom => {
                                            return <MeetingRoomGroupContainer>
                                                <MeetingRoomGroup key={conferenceRoom.id} source={undefined} accessStatus={(props.conferenceRoomAccessList.find((e: UserRestrictedAreaAccess) => e.restrictedAreaId === conferenceRoom.id))?.status || " "} meetingRoomGroupConfig={conferenceRoom} roomType="conferenceroom" />
                                            </MeetingRoomGroupContainer>
                                        })}
                                    </TileRow>
                                </RowTileParent>
                            )
                        }

                        return null
                    })
                }
            </Col>
            <PrivacyModal />
        </Row>
    )
}
/* #endregion */

/* #region  Company social media */

const SocialMediaIcon = styled.div`
    cursor: pointer;

    a{
        color: unset;
        text-decoration: none;
    }
`

interface CompanySocialMediaProps {
    socialMedias: SocialMedia[]
}

const CompanySocialMedia: React.FunctionComponent<CompanySocialMediaProps> = (props) => {

    const facebook: SocialMedia | undefined = props.socialMedias.find((socialMedia: SocialMedia) => socialMedia.type === SocialMediaType.FACEBOOK)
    const twitter: SocialMedia | undefined = props.socialMedias.find((socialMedia: SocialMedia) => socialMedia.type === SocialMediaType.TWITTER)
    const instagram: SocialMedia | undefined = props.socialMedias.find((socialMedia: SocialMedia) => socialMedia.type === SocialMediaType.INSTAGRAM)
    const youTube: SocialMedia | undefined = props.socialMedias.find((socialMedia: SocialMedia) => socialMedia.type === SocialMediaType.YOUTUBE)
    const linkedIn: SocialMedia | undefined = props.socialMedias.find((socialMedia: SocialMedia) => socialMedia.type === SocialMediaType.LINKEDIN)
    const xing: SocialMedia | undefined = props.socialMedias.find((socialMedia: SocialMedia) => socialMedia.type === SocialMediaType.XING)
    const pinterest: SocialMedia | undefined = props.socialMedias.find((socialMedia: SocialMedia) => socialMedia.type === SocialMediaType.PINTEREST)

    function handleLinkClick(link: string, e: React.MouseEvent<HTMLAnchorElement>) {
        if (!link)
            e.currentTarget.removeAttribute("href");
    }
    if (!facebook && !twitter && !instagram && !youTube && !linkedIn && !xing && !pinterest)
        return <></>

    return (
        <div className="d-flex mt-5 mb-5">

            {branding.organizationDetailPageContent.availableSocialMedias.map((item, index) => {
                if (item === SocialMediaType.FACEBOOK) {
                    return <>
                        {facebook && facebook.link && facebook.link.length > 0 &&
                            <SocialMediaIcon key={index} className="mr-5">
                                <a href={facebook.link} onClick={(e) => handleLinkClick(facebook.link, e)} target="_blank" rel="noopener noreferrer">{branding.socialMediaIcons.useOriginalSocialMediaIcons ? IconFacebookOriginal({ width: '20', height: '20' }) : IconFacebook({ fill: branding.sideIconBar.sideIconColorDark, width: "20", height: "20" })}</a>
                            </SocialMediaIcon>
                        }
                    </>
                }

                if (item === SocialMediaType.TWITTER) {
                    return <>
                        {twitter && twitter.link && twitter.link.length > 0 &&
                            <SocialMediaIcon key={index} className="mr-5">
                                <a href={twitter.link} onClick={(e) => handleLinkClick(twitter.link, e)} target="_blank" rel="noopener noreferrer">{branding.socialMediaIcons.useOriginalSocialMediaIcons ? IconTwitter({ width: '20', height: '20' }) : IconTwitter({ fill: branding.sideIconBar.sideIconColorDark, width: "20", height: "20" })}</a>
                            </SocialMediaIcon>
                        }
                    </>
                }

                if (item === SocialMediaType.LINKEDIN) {
                    return <>
                        {linkedIn && linkedIn.link && linkedIn.link.length > 0 &&
                            <SocialMediaIcon key={index} className="mr-5">
                                <a href={linkedIn.link} onClick={(e) => handleLinkClick(linkedIn.link, e)} target="_blank" rel="noopener noreferrer">{branding.socialMediaIcons.useOriginalSocialMediaIcons ? IconLinkedInOriginal({ width: '20', height: '20' }) : IconLinkedIn({ fill: branding.sideIconBar.sideIconColorDark, width: "20", height: "20" })}</a>
                            </SocialMediaIcon>
                        }
                    </>
                }

                if (item === SocialMediaType.XING) {
                    return <>
                        {xing && xing.link && xing.link.length > 0 &&
                            <SocialMediaIcon key={index} className="mr-5">
                                <a href={xing.link} onClick={(e) => handleLinkClick(xing.link, e)} target="_blank" rel="noopener noreferrer">{branding.socialMediaIcons.useOriginalSocialMediaIcons ? IconXingOriginal({ width: '20', height: '20' }) : IconXing({ fill: branding.sideIconBar.sideIconColorDark, width: "20", height: "20" })}</a>
                            </SocialMediaIcon>
                        }
                    </>
                }

                if (item === SocialMediaType.INSTAGRAM) {
                    return <>
                        {instagram && instagram.link && instagram.link.length > 0 &&
                            <SocialMediaIcon key={index} className="mr-5">
                                <a href={instagram.link} onClick={(e) => handleLinkClick(instagram.link, e)} target="_blank" rel="noopener noreferrer">{branding.socialMediaIcons.useOriginalSocialMediaIcons ? IconInstagramOriginal({ width: '20', height: '20' }) : IconInstagram({ fill: branding.sideIconBar.sideIconColorDark, width: "20", height: "20" })}</a>
                            </SocialMediaIcon>
                        }
                    </>
                }

                if (item === SocialMediaType.YOUTUBE) {
                    return <>
                        {youTube && youTube.link && youTube.link.length > 0 &&
                            <SocialMediaIcon key={index} className="mr-5">
                                <a href={youTube.link} onClick={(e) => handleLinkClick(youTube.link, e)} target="_blank" rel="noopener noreferrer">{branding.socialMediaIcons.useOriginalSocialMediaIcons ? IconYouTubeOriginal({ width: '20', height: '20' }) : IconYouTube({ fill: branding.sideIconBar.sideIconColorDark, width: "20", height: "20" })}</a>
                            </SocialMediaIcon>
                        }
                    </>
                }

                if (item === SocialMediaType.PINTEREST) {
                    return <>
                        {pinterest && pinterest.link && pinterest.link.length > 0 &&
                            <SocialMediaIcon key={index} className="mr-5">
                                <a href={pinterest.link} onClick={(e) => handleLinkClick(pinterest.link, e)} target="_blank" rel="noopener noreferrer">{branding.socialMediaIcons.useOriginalSocialMediaIcons ? IconPinterestOrginal({ width: '20', height: '20' }) : IconPinterest({ fill: branding.sideIconBar.sideIconColorDark, width: "20", height: "20" })}</a>
                            </SocialMediaIcon>
                        }
                    </>
                }

                return null
            })
            }

        </div>
    )
}

/* #endregion */

/* #region  Founding year, size, industry*/
const CompanyDetailsRoot = styled.div`
    display: flex;
    justify-content: space-between;
     flex-wrap: wrap;
    flex: 1;
    line-height: 17px;
    font-size: 12px;
    font-weight: 400;
    font-style: normal;
    font-family: ${branding.font1};
`
const CompanyAdditionalInfoRoot = styled.div`
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    flex: 1;
    line-height: 17px;
    font-size: 12px;
    font-weight: 400;
    font-style: normal;
    font-family: ${branding.font1};

    & p {
        margin-bottom: 0
    }

`

const CompanyAdditionalInfoDiv = styled.div`
    line-height: 17px;
    font-size: 12px;
    font-weight: 400;
    font-style: normal;
    font-family: ${branding.font1};


    & p {
        margin-bottom: 0;
    }

    & a {
        font-weight: normal !important;
    }
`


interface CompanyDetailsProps {
    company: Company

}
const CompanyDetails: React.FunctionComponent<CompanyDetailsProps> = (props) => {
    const strings = useLanguageState().getStrings();
    const company = props.company
    return (
        <CompanyDetailsRoot>
            <CompanyAddressPhoneEmail company={company} />
            <CompanyAdditionalInfoRoot>
                <CompanyAdditionalInfoDiv style={{ flex: 1, display: 'flex', flexDirection: 'row' }}>
                    <div style={{ paddingRight: '10px' }}>
                        {(company.foundingYear || company.size || company.industry) && IconInfo({ fill: branding.sideIconBar.sideIconColorDark, width: '20', height: '20' })}
                    </div>
                    <div>
                        {company.foundingYear && <p>{strings.organizationDetailPageContent.foundingYearTitle}: {company.foundingYear}</p>}
                        {company.size && <p>{strings.organizationDetailPageContent.companySizeTitle}: {company.size}</p>}
                        {company.industry && <p>{strings.organizationDetailPageContent.industryTitle}: {company.industry}</p>}
                    </div>
                </CompanyAdditionalInfoDiv>
            </CompanyAdditionalInfoRoot>
        </CompanyDetailsRoot>
    )
}


/* #endregion */


/* #region  Team and session template component */
interface ScrollableDivProps {
    width: string
}
export const ScrollableDiv = styled.div<ScrollableDivProps>`
    max-height: 350px;
    width: ${props => props.width};
    padding: 0;
    overflow-y: auto;
    overflow-x: hidden;
    -ms-overflow-style: none;  
    scrollbar-width: none; 
    ::-webkit-scrollbar {
        display: none;
    }
`
const SessionsContentCol = styled(Col)`
    border-bottom: ${branding.mainBorder ? branding.mainBorder : '1px solid #d9d9d9'};
    padding: 25px 0;
`

const ScrollerShadowTop = styled.div`
    position: absolute;
    width: 100%;
    height: 10%;
    background: transparent;
    background: ${branding.primaryScrollWhiteShadowTTB};
    left: 0;
    top: 0;
    z-index: 2;
`

const ScrollerShadowBottom = styled.div`
    position: absolute;
    width: 100%;
    height: 10%;
    background: transparent;
    background: ${branding.primaryScrollWhiteShadowTTB};
    left: 0;
    bottom: 0;
    z-index: 2;
    transform: rotate(180deg);
`


interface TeamSessionsTemplateProps {
    team?: Person[]
    sessions?: EventDate[]
    coupons?: any[]
    type: "team" | "session" | "coupon"
    organizationId: string
    organizationName?: string
    organization: Company
    onAccessDenied?: () => void,
    past?: boolean
    live?: boolean
    hideShadows?: boolean
}

const TeamSessionsTemplate: React.FunctionComponent<TeamSessionsTemplateProps> = (props) => {
    const strings = useLanguageState().getStrings();
    const scrollRef = useRef<HTMLDivElement>(null)
    const [position, setPosition] = useState(0);
    const [value, setValue] = useState(0);
    const maxScrollRange = scrollRef.current?.scrollHeight! - scrollRef.current?.clientHeight!;
    const width = maxScrollRange !== 0 ? "95%" : "100%";
    const [staff, setStaff] = useState<Staff[] | undefined>()
    const userState = useLoggedInState()
    const profileId = userState.user()?.profileId
    const { showPrivacyPolicyModal, PrivacyModal } = usePrivacyPolicyModal()
    // Are shadows active
    const [isShadowTop, setIsShadowTop] = useState<boolean>(false)
    const [isShadowBottom, setIsShadowBottom] = useState<boolean>(false)

    let sessions = props.sessions
    const location = useLocation()
    let currentLocation = location.pathname;
    let coupons = props.coupons

    useEffect(() => {
        scrollRef.current?.scrollTo(0, position)
    }, [position])

    // DETERMING IF THE SHADOWS SHOULD BE VISIBLE
    useEffect(() => {
        if ((scrollRef.current!.scrollWidth === scrollRef.current!.clientHeight) || props.hideShadows) return
        value === 0 ? setIsShadowTop(false) : setIsShadowTop(true)
        maxScrollRange - value < 40 ? setIsShadowBottom(false) : setIsShadowBottom(true)
        // eslint-disable-next-line
    }, [value, maxScrollRange])

    useEffect(() => {
        setStaff(props.team?.filter(person => person.visible).map(person => {
            return { firstName: person.firstName, lastName: person.lastName, id: person.id, type: StaffType.PERSON, logoUrl: person.logoUrl || person.userLogoUrl, position: person.position, company: person.organization, sotUserId: person.userId, visible: person.visible, showroomStandby: person.showroomStandby, title: person.title, categories: person.categories }
        }))
    }, [props.organizationId]) //eslint-disable-line

    if ((props.type === "team" && (!props.team || props.team.length === 0)) ||
        (props.type === "session" && (!props.sessions || props.sessions.length === 0)))
        return null

    const staffLength = staff !== undefined ? staff.length : 0
    let headerTitle = props.type === "team" ?
        (branding.organizationDetailPageContent.teamSectionTitleVisible ? strings.organizationDetailPageContent.teamSectionTitle : props.organizationName) + " (" + staffLength + ")" :
        (props.type === "session" ? strings.organizationDetailPageContent.upcomingSessionsTitle + " (" + props.sessions!.length + ")" : strings.sideIconBar.goodiebagMenuText)

    if (props.live)
        headerTitle = strings.organizationDetailPageContent.liveSessionsTitle + " (" + props.sessions!.length + ")"

    if (props.past) {
        headerTitle = props.type === "team" ?
            props.organizationName + " (" + staffLength + ")" : strings.organizationDetailPageContent.pastSessionsTitle + " (" + props.sessions!.length + ")"
    }




    return (
        <Col xs={12}>
            <Row style={{ marginRight: "5px", marginLeft: "5px" }}>
                <Col xs={12} className="p-0 mb-4">
                    <HeaderTitle>{headerTitle}</HeaderTitle>
                </Col>
                <div style={{ width: '100%', position: 'relative' }}>
                    <ScrollableDiv ref={scrollRef} onScroll={() => setValue(scrollRef.current?.scrollTop!)} width={width}>

                        {staff && staff.map((person, index) => {
                            return <StaffEntry onAccessDenied={() => { if (props.onAccessDenied) { props.onAccessDenied() } }} key={index} staff={person} organizationId={props.organizationId} backOffice={false} />
                        })}

                        {sessions && sessions.map(session => {
                            return (
                                <SessionsContentCol xs={12} key={session.name}>
                                    <SessionData session={session} />
                                </SessionsContentCol>
                            )
                        })}
                        {coupons && coupons.map(coupon => {
                            return (
                                <CouponData key={coupon.id} companyId={props.organizationId} {...coupon} onAction={() => {
                                    showPrivacyPolicyModal(props.organization, (accepted) => {
                                        if (coupon.url !== null && !(coupon.url?.includes(currentLocation))) {
                                            window.open(coupon.url, "_blank")
                                            trackVisit(profileId!, props.organizationId, "COUPON", coupon.title)
                                        }
                                    })
                                }} />
                            )
                        })}
                        {isShadowTop && !props.hideShadows && <ScrollerShadowTop />}
                        {isShadowBottom && !props.hideShadows && <ScrollerShadowBottom />}
                    </ScrollableDiv>
                </div>
                <ScrollBarComponent value={value} maxScrollRange={maxScrollRange ? maxScrollRange : 0} onScroll={(val: number) => { setPosition(val); setValue(val); }} isVertical={true}></ScrollBarComponent>
            </Row>
            <PrivacyModal />
        </Col>
    )
}

const SessionRow = styled(Row)`
    line-height: 30px;
`
const ShareBookmarkDiv = styled.div`
    display: flex; 
    justify-content: flex-end;
    font-size: 20px;
`
const ShareIcon = styled.div`
    cursor: pointer;
    margin-right: 20px;
    color: ${branding.primaryColor}; 
`

interface SessionDataProps {
    session: EventDate
}

export const SessionData: React.FunctionComponent<SessionDataProps> = (props) => {
    const languageState = useLanguageState()
    const strings = languageState.getStrings()
    const lang = languageState.getLanguage()

    const { showRecommendOrganizationModal, OrganizationModal } = useRecommendOrganizationModal()

    function getFormattedDate(date: string, start: string) {
        return format(moment(date).toDate(), strings.eventTiming.eventDaysFormatPatternEvtDetailPage, { locale: lang === 'de' ? germanLocale : null }) + (lang === "de" ? " um " : " at ") + start //have to find a better way to do this but this works for now
    }
    const isLive = isEventDateLive(props.session)

    const categoriesState = useCategoriesState()
    const [pointsBadgeData, setPointsBadgeData] = useState<Category>();

    useEffect(() => {
        if (categoriesState.getCategories().length)
            setPointsBadgeData(categoriesState.getCategoryByAlias(branding.programSchedule.pointsBadgeCategoryAlias))
        // eslint-disable-next-line
    }, [])

    return (
        <DetailNavLink
            name={props.session.name}
            id={isLive && props.session.channel ? props.session.channel.id : props.session.id}
            type={isLive && props.session.channel ? "channel" : "eventdate"}>
            <SessionRow>
                <Col style={{ paddingRight: '0', flex: '0 0 20px', marginRight: '20px' }} xs={1}>{IconVideo({ fill: branding.sideIconBar.sideIconColorDark })}</Col>
                <Col xs={9} style={{ paddingLeft: '0' }}>
                    <FormattedDatePar>{props.session.name}</FormattedDatePar>
                    <FormattedDateExtensionDiv>{getFormattedDate(props.session.date, props.session.start)}</FormattedDateExtensionDiv>
                    <FormattedDateExtensionDiv>{props.session.location}</FormattedDateExtensionDiv>
                    {(pointsBadgeData && props.session.categories?.map(x => x.alias)?.includes(branding.programSchedule.pointsBadgeCategoryAlias)) &&
                        <PointsBadge
                            className={'ml-2'}
                            textColor={pointsBadgeData.colorText}
                            fillColor={pointsBadgeData.colorFill}
                            borderColor={pointsBadgeData.colorBorder} >
                            {strings.programSchedule.pointsBadgeText}
                        </PointsBadge>}
                    {props.session.isPrivate && <FormattedDateExtensionDiv>
                        {IconLock({ 'fill': branding.sideIconBar.bookmarksIconText, width: '18', height: '18' })}
                    </FormattedDateExtensionDiv>}
                </Col>
                <Col xs={2} style={{ marginLeft: 'calc(8.333333% - 55px)' }}>
                    <ShareBookmarkDiv>
                        <ShareIcon onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            showRecommendOrganizationModal(ShareTargetType.EVENTDATE, props.session.id, buildDetailLink(props.session.id, props.session.name, "eventdate"))
                        }}>
                            {IconShare({ fill: branding.sideIconBar.sideIconColorDark })}
                        </ShareIcon>
                        <BookmarkWithToggle newBookmarkItem={true} fontSize={"25px"} color={branding.sideIconBar.sideIconColorDark} stroke={branding.sideIconBar.sideIconColorDark} favIconBasic={true} type="eventdate" id={props.session.id} />
                    </ShareBookmarkDiv>
                </Col>
            </SessionRow>
            <OrganizationModal />
        </DetailNavLink>
    )
}



/* #region Coupon Data */
const CouponDetails = styled.div`
    cursor: pointer;
    padding-left: 10px;
`

const CouponNameLabel = styled.div`
    font-weight: bold;
    font-size: 16px;
    margin-bottom: 10px;
    line-height: 1rem;
    margin-top: 2px;
`

const CouponDescriptionLabel = styled.div`
    font-size: 14px;
    line-height: 0.8rem;
`

interface CouponDataProps {
    id: string;
    companyId: string;
    title: string;
    teaser: string;
    description: string;
    url: string;
    onAction: () => void
}

const CouponData: React.FunctionComponent<CouponDataProps> = (props) => {
    const location = useLocation()
    let currentLocation = location.pathname;
    const goToCoupon = () => {
        props.onAction()
    }

    return (
        <SessionsContentCol>
            <SessionRow style={{ pointerEvents: props.url === null || (props.url?.includes(currentLocation)) ? "none" : "auto" }} onClick={() => goToCoupon()}>
                <Col style={{ paddingRight: '0', flex: '0 0 20px' }} xs={1}>
                    {IconCoupon({ fill: branding.sideIconBar.sideIconColorDark ?? branding.mainInfoColor })}
                </Col>
                <Col xs={11} >
                    <CouponDetails>
                        <CouponNameLabel>{props.title}</CouponNameLabel>
                        <CouponDescriptionLabel>{props.teaser || props.description}</CouponDescriptionLabel>
                    </CouponDetails>
                </Col>
            </SessionRow>
        </SessionsContentCol>
    )
}
/* #endregion */

/* #region  Links */
const LinkNameUrl = styled.p`
    font-size: 16px;
    display: inline-block;
    cursor: pointer;
    white-space: normal;
    word-break: break-word;
    margin-bottom: 0px;

    @media (max-width: 1600px) {
        font-size: 16px;   
    }

    &:hover {
        opacity: 0.3;
    }
`
const LinkRow = styled(Row)`
    display: flex;
    justify-content: center;
    align-items: center;
    border-bottom: ${branding.mainBorder ? branding.mainBorder : '1px solid #d9d9d9'};
    padding: 25px 0;
`
const CopyToClipboardDiv = styled.div`
    cursor: pointer;
`

interface LinksProps {
    organizationId?: string
    organization?: Company
    links: Link[]
    tooltipMessage: string
    colXs?: number
}

export const Links: React.FunctionComponent<LinksProps> = (props) => {
    const strings = useLanguageState().getStrings();
    const { showPrivacyPolicyModal, PrivacyModal } = usePrivacyPolicyModal()
    const loggedInUserId = useLoggedInState().user()?.profileId
    if (!props.links || props.links.length === 0)
        return null

    return (
        <Col xs={props.colXs ?? 12}>
            <Row className="mb-4" style={{ marginLeft: "5px", marginRight: "5px" }}>
                <HeaderTitle>{props.links.length > 1 ? strings.organizationDetailPageContent.linksTitle + " (" + props.links.length + ")" : "Link (1)"}</HeaderTitle>
            </Row>
            {
                props.links.map((link, i) => <LinkItem link={link} key={i} onCopyLink={() => {
                    if (props.organizationId)
                        showPrivacyPolicyModal(props.organization!, (accepted) => {
                            navigator.clipboard.writeText(link.url)
                        })
                    else navigator.clipboard.writeText(link.url)
                }}
                    onFollowLink={() => {
                        if (props.organizationId)
                            showPrivacyPolicyModal(props.organization!, (accepted) => {
                                trackVisit(loggedInUserId!, props.organizationId!, "LINK", link.url)
                                window.open(link.url, "_blank")
                            })
                        else window.open(link.url, "_blank")
                    }}
                    tooltipMessage={props.tooltipMessage} />)
            }
            <PrivacyModal />
        </Col >
    )
}


interface LinkProps {
    link: Link
    tooltipMessage: string
    onCopyLink: () => void
    onFollowLink: () => void
}

const LinkItem: React.FunctionComponent<LinkProps> = (props) => {
    const [text, setText] = useState(props.link.text)
    const [tooltip, setTooltip] = useState(props.tooltipMessage)

    useEffect(() => {
        setText(props.link.text)
    }, [props.link])


    return (
        <LinkRow className="mt-3 mx-0">
            <Col style={{ paddingRight: '0', flex: '0 0 20px', marginRight: '25px' }} xs={1} className="p-0">{IconLink({ fill: branding.sideIconBar.sideIconColorDark })}</Col>
            <Col style={{ paddingLeft: '0' }} xs={9} onMouseEnter={() => setText(props.link.url)} onMouseLeave={() => setText(props.link.text)} >
                <LinkNameUrl onClick={props.onFollowLink}>{text}</LinkNameUrl>
            </Col>
            <Col xs={2} style={{ marginLeft: 'calc(8.333333% - 55px)' }} className="d-flex justify-content-end p-0">
                <OverlayTrigger
                    placement="left"
                    delay={{ show: 250, hide: 400 }}
                    overlay={
                        <Tooltip id="tooltip" style={{ fontFamily: branding.font1 }}>
                            {tooltip}
                        </Tooltip>}>
                    <CopyToClipboardDiv onClick={() => {
                        props.onCopyLink()
                        setTooltip('copied')
                    }} onMouseLeave={() =>
                        setTimeout(() => {
                            setTooltip(props.tooltipMessage)
                        }, 500)
                    }>{IconShareLink({ fill: branding.sideIconBar.sideIconColorDark })}</CopyToClipboardDiv>
                </OverlayTrigger>
            </Col>
        </LinkRow>
    )
}

const IframeButtonArea = styled.div`
    display: flex;
    flex-flow: row;
    justify-content: space-evenly;
`

const IframeButton = styled.button`
    display: flex;
    width: 150px;
    height: 50px;
    font-size: 14px;
    align-items: center;
    border: 1px solid;
    background: 0;
    justify-content: center;
    border-radius: 5px;
`
interface IframeSectionProps {
    src: string
    title: string
}

const IframeSection: React.FunctionComponent<IframeSectionProps> = (props) => {

    const strings = useLanguageState().getStrings();
    const iframeRef = useRef<HTMLIFrameElement>(null);

    function openFullscreen() {
        if (iframeRef?.current?.requestFullscreen) {
            iframeRef.current.requestFullscreen();
        } else {
            // this only happens in safari case
            const iframeSafari = iframeRef.current as unknown as HTMLElement & {
                webkitRequestFullscreen(): Promise<void>
            };
            if (iframeSafari?.webkitRequestFullscreen)
                iframeSafari.webkitRequestFullscreen()
        }
    }

    function openInNewTab() {
        window.open(props.src, "_blank")
    }

    return <Row className="mt-5 pb-5 d-flex flex-column" style={{ marginLeft: "5px", marginRight: "5px" }}>
        <HeaderTitle>{strings.organizationDetailPageContent.showcase}</HeaderTitle>
        <iframe id="orgaIframe" ref={iframeRef} className="mt-5" style={{ height: "600px", borderRadius: "5px", border: branding.organizationDetailPageContent.iframeBorder }} title={props.title} src={props.src} allowFullScreen={true}></iframe>
        <IframeButtonArea className="mx-auto w-50 mt-3">
            <IframeButton onClick={() => openFullscreen()}>{strings.organizationDetailPageContent.fullscreenButton}</IframeButton>
            <IframeButton onClick={() => openInNewTab()}>{strings.organizationDetailPageContent.newTabButton}</IframeButton>
        </IframeButtonArea>
    </Row>
}
/* #endregion */
